import React, { useState, useEffect } from 'react';
import { 
  MapPin, Calendar, Send, Truck, Heart, Users, ArrowRight,
  CheckCircle, Clock, Info, PawPrint, Building2, Phone, Mail,
  ChevronDown, ChevronUp, Filter
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// German cities with approximate coordinates for the map visualization
const CITY_COORDS = {
  'berlin': { lat: 52.52, lng: 13.405, name: 'Berlin' },
  'hamburg': { lat: 53.551, lng: 9.993, name: 'Hamburg' },
  'münchen': { lat: 48.137, lng: 11.576, name: 'München' },
  'köln': { lat: 50.937, lng: 6.96, name: 'Köln' },
  'frankfurt': { lat: 50.11, lng: 8.682, name: 'Frankfurt' },
  'düsseldorf': { lat: 51.227, lng: 6.773, name: 'Düsseldorf' },
  'dortmund': { lat: 51.514, lng: 7.468, name: 'Dortmund' },
  'essen': { lat: 51.455, lng: 7.011, name: 'Essen' },
  'stuttgart': { lat: 48.775, lng: 9.181, name: 'Stuttgart' },
  'leipzig': { lat: 51.34, lng: 12.374, name: 'Leipzig' },
  'bremen': { lat: 53.079, lng: 8.801, name: 'Bremen' },
  'dresden': { lat: 51.05, lng: 13.737, name: 'Dresden' },
  'hannover': { lat: 52.375, lng: 9.732, name: 'Hannover' },
  'nürnberg': { lat: 49.452, lng: 11.076, name: 'Nürnberg' },
  'duisburg': { lat: 51.435, lng: 6.762, name: 'Duisburg' },
  'bochum': { lat: 51.482, lng: 7.216, name: 'Bochum' },
  'wuppertal': { lat: 51.256, lng: 7.15, name: 'Wuppertal' },
  'bielefeld': { lat: 52.021, lng: 8.531, name: 'Bielefeld' },
  'bonn': { lat: 50.737, lng: 7.098, name: 'Bonn' },
  'münster': { lat: 51.961, lng: 7.626, name: 'Münster' },
  'karlsruhe': { lat: 49.006, lng: 8.403, name: 'Karlsruhe' },
  'mannheim': { lat: 49.487, lng: 8.466, name: 'Mannheim' },
  'augsburg': { lat: 48.371, lng: 10.898, name: 'Augsburg' },
  'wiesbaden': { lat: 50.082, lng: 8.24, name: 'Wiesbaden' },
  'hagen': { lat: 51.361, lng: 7.474, name: 'Hagen' },
  'aachen': { lat: 50.775, lng: 6.083, name: 'Aachen' },
  'rostock': { lat: 54.092, lng: 12.099, name: 'Rostock' },
  'kiel': { lat: 54.323, lng: 10.122, name: 'Kiel' },
  'lübeck': { lat: 53.865, lng: 10.686, name: 'Lübeck' },
  'erfurt': { lat: 50.978, lng: 11.029, name: 'Erfurt' },
  'magdeburg': { lat: 52.12, lng: 11.627, name: 'Magdeburg' },
  'freiburg': { lat: 47.999, lng: 7.842, name: 'Freiburg' },
  'mainz': { lat: 50.0, lng: 8.271, name: 'Mainz' },
  'saarbrücken': { lat: 49.24, lng: 6.997, name: 'Saarbrücken' },
  'potsdam': { lat: 52.39, lng: 13.064, name: 'Potsdam' },
  'osnabrück': { lat: 52.279, lng: 8.047, name: 'Osnabrück' },
  'oldenburg': { lat: 53.143, lng: 8.213, name: 'Oldenburg' },
  'kassel': { lat: 51.312, lng: 9.479, name: 'Kassel' },
  'regensburg': { lat: 49.013, lng: 12.101, name: 'Regensburg' }
};

const ComboTransportPage = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [filterAnimalType, setFilterAnimalType] = useState('all');
  
  const [formData, setFormData] = useState({
    organizationName: '',
    contactPerson: '',
    email: '',
    phone: '',
    originCity: '',
    originAddress: '',
    destinationCity: '',
    destinationAddress: '',
    dateFrom: '',
    dateTo: '',
    flexibleDates: true,
    animalType: 'Hund',
    animalCount: 1,
    notes: ''
  });

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await axios.get(`${API}/combo-transports/public`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/combo-transports/request`, {
        ...formData,
        dateFrom: new Date(formData.dateFrom).toISOString(),
        dateTo: new Date(formData.dateTo).toISOString()
      });
      
      toast.success(`Anfrage erfolgreich eingereicht! ID: ${response.data.requestId}`);
      setShowForm(false);
      setFormData({
        organizationName: '',
        contactPerson: '',
        email: '',
        phone: '',
        originCity: '',
        originAddress: '',
        destinationCity: '',
        destinationAddress: '',
        dateFrom: '',
        dateTo: '',
        flexibleDates: true,
        animalType: 'Hund',
        animalCount: 1,
        notes: ''
      });
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einreichen der Anfrage');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCityCoords = (cityName) => {
    const normalized = cityName.toLowerCase().trim();
    return CITY_COORDS[normalized] || null;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredRequests = filterAnimalType === 'all' 
    ? requests 
    : requests.filter(r => r.animalType.toLowerCase() === filterAnimalType.toLowerCase());

  // SVG Map dimensions
  const mapWidth = 800;
  const mapHeight = 600;
  
  // Germany bounds (approximate)
  const bounds = {
    minLat: 47.2,
    maxLat: 55.1,
    minLng: 5.8,
    maxLng: 15.1
  };

  const latLngToXY = (lat, lng) => {
    const x = ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * mapWidth;
    const y = mapHeight - ((lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * mapHeight;
    return { x, y };
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-500 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Heart className="h-10 w-10" />
            <Truck className="h-10 w-10" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Kombi-Transport für Tierschutzvereine
          </h1>
          <p className="text-lg text-green-100 max-w-2xl mx-auto">
            Sparen Sie Transportkosten durch intelligente Routenplanung. 
            Melden Sie Ihren Transport an und finden Sie Kombinationsmöglichkeiten mit anderen Vereinen.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="h-6 w-6" />
              </div>
              <h3 className="font-bold text-green-800 mb-2">1. Anfrage einreichen</h3>
              <p className="text-sm text-green-700">
                Melden Sie Ihren geplanten Transport mit Route und Zeitraum an.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="font-bold text-blue-800 mb-2">2. Kombination finden</h3>
              <p className="text-sm text-blue-700">
                Wir suchen passende Transporte anderer Vereine für Ihre Route.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-6 w-6" />
              </div>
              <h3 className="font-bold text-orange-800 mb-2">3. Kosten teilen</h3>
              <p className="text-sm text-orange-700">
                Bei einer Kombination teilen Sie die Anfahrtskosten und sparen!
              </p>
            </CardContent>
          </Card>
        </div>

        {/* CTA Button */}
        <div className="text-center mb-8">
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-green-600 hover:bg-green-700 text-lg px-8 py-6"
            data-testid="new-transport-request-btn"
          >
            {showForm ? (
              <>
                <ChevronUp className="h-5 w-5 mr-2" />
                Formular schließen
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Neue Transport-Anfrage
              </>
            )}
          </Button>
        </div>

        {/* Request Form */}
        {showForm && (
          <Card className="mb-8 border-green-200">
            <CardHeader className="bg-green-50">
              <CardTitle className="flex items-center gap-2 text-green-800">
                <Building2 className="h-5 w-5" />
                Transport-Anfrage einreichen
              </CardTitle>
              <CardDescription>
                Alle Felder mit * sind Pflichtfelder. Ihre Kontaktdaten werden nicht öffentlich angezeigt.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Organization Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="organizationName">Tierschutzverein / Organisation *</Label>
                    <Input
                      id="organizationName"
                      value={formData.organizationName}
                      onChange={(e) => setFormData({...formData, organizationName: e.target.value})}
                      placeholder="z.B. Tierschutzverein Hagen e.V."
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactPerson">Ansprechpartner *</Label>
                    <Input
                      id="contactPerson"
                      value={formData.contactPerson}
                      onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                      placeholder="Vor- und Nachname"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-Mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="verein@example.de"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefon *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="0123 456789"
                      required
                    />
                  </div>
                </div>

                {/* Route */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-green-600" />
                    Route
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="originCity">Startort (Stadt) *</Label>
                      <Input
                        id="originCity"
                        value={formData.originCity}
                        onChange={(e) => setFormData({...formData, originCity: e.target.value})}
                        placeholder="z.B. Köln"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="originAddress">Startadresse (optional)</Label>
                      <Input
                        id="originAddress"
                        value={formData.originAddress}
                        onChange={(e) => setFormData({...formData, originAddress: e.target.value})}
                        placeholder="Straße, PLZ"
                      />
                    </div>
                    <div>
                      <Label htmlFor="destinationCity">Zielort (Stadt) *</Label>
                      <Input
                        id="destinationCity"
                        value={formData.destinationCity}
                        onChange={(e) => setFormData({...formData, destinationCity: e.target.value})}
                        placeholder="z.B. Frankfurt"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="destinationAddress">Zieladresse (optional)</Label>
                      <Input
                        id="destinationAddress"
                        value={formData.destinationAddress}
                        onChange={(e) => setFormData({...formData, destinationAddress: e.target.value})}
                        placeholder="Straße, PLZ"
                      />
                    </div>
                  </div>
                </div>

                {/* Date Range */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-green-600" />
                    Zeitraum
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="dateFrom">Frühestens ab *</Label>
                      <Input
                        id="dateFrom"
                        type="date"
                        value={formData.dateFrom}
                        onChange={(e) => setFormData({...formData, dateFrom: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="dateTo">Spätestens bis *</Label>
                      <Input
                        id="dateTo"
                        type="date"
                        value={formData.dateTo}
                        onChange={(e) => setFormData({...formData, dateTo: e.target.value})}
                        min={formData.dateFrom || new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.flexibleDates}
                          onChange={(e) => setFormData({...formData, flexibleDates: e.target.checked})}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">Flexible Termine</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Animal Info */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <PawPrint className="h-5 w-5 text-green-600" />
                    Tier-Informationen
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="animalType">Tierart *</Label>
                      <select
                        id="animalType"
                        value={formData.animalType}
                        onChange={(e) => setFormData({...formData, animalType: e.target.value})}
                        className="w-full h-10 px-3 rounded-md border border-gray-300"
                        required
                      >
                        <option value="Hund">Hund</option>
                        <option value="Katze">Katze</option>
                        <option value="Kleintier">Kleintier</option>
                        <option value="Vogel">Vogel</option>
                        <option value="Sonstige">Sonstige</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="animalCount">Anzahl Tiere *</Label>
                      <Input
                        id="animalCount"
                        type="number"
                        min="1"
                        max="20"
                        value={formData.animalCount}
                        onChange={(e) => setFormData({...formData, animalCount: parseInt(e.target.value) || 1})}
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label htmlFor="notes">Zusätzliche Hinweise (optional)</Label>
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Besonderheiten, spezielle Anforderungen, etc."
                      className="w-full h-24 px-3 py-2 rounded-md border border-gray-300 resize-none"
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="border-t pt-4 flex justify-end gap-4">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Abbrechen
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-green-600 hover:bg-green-700"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Wird eingereicht...' : 'Anfrage einreichen'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Map Section */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-green-600" />
                  Aktuelle Transport-Anfragen
                </CardTitle>
                <CardDescription>
                  Übersicht aller offenen Anfragen (anonymisiert)
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <select
                  value={filterAnimalType}
                  onChange={(e) => setFilterAnimalType(e.target.value)}
                  className="h-9 px-3 rounded-md border border-gray-300 text-sm"
                >
                  <option value="all">Alle Tierarten</option>
                  <option value="hund">Hunde</option>
                  <option value="katze">Katzen</option>
                  <option value="kleintier">Kleintiere</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12 text-gray-500">
                Lade Anfragen...
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Truck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Aktuell keine offenen Anfragen</p>
                <p className="text-sm mt-2">Seien Sie der Erste und reichen Sie eine Anfrage ein!</p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* SVG Map */}
                <div className="bg-gray-100 rounded-lg p-4 overflow-hidden">
                  <svg
                    viewBox={`0 0 ${mapWidth} ${mapHeight}`}
                    className="w-full h-auto"
                    style={{ maxHeight: '500px' }}
                  >
                    {/* Germany outline (simplified) */}
                    <path
                      d="M400,50 L480,80 L520,150 L550,200 L580,280 L600,350 L580,420 L520,480 L450,520 L380,550 L300,540 L220,500 L180,440 L150,380 L120,300 L140,220 L180,150 L250,100 L320,70 Z"
                      fill="#e5e7eb"
                      stroke="#9ca3af"
                      strokeWidth="2"
                    />
                    
                    {/* City dots */}
                    {Object.entries(CITY_COORDS).map(([key, city]) => {
                      const { x, y } = latLngToXY(city.lat, city.lng);
                      return (
                        <g key={key}>
                          <circle
                            cx={x}
                            cy={y}
                            r="4"
                            fill="#6b7280"
                            opacity="0.5"
                          />
                          <text
                            x={x}
                            y={y - 8}
                            textAnchor="middle"
                            fontSize="10"
                            fill="#6b7280"
                          >
                            {city.name}
                          </text>
                        </g>
                      );
                    })}
                    
                    {/* Route lines */}
                    {filteredRequests.map((req, idx) => {
                      const originCoords = getCityCoords(req.originCity);
                      const destCoords = getCityCoords(req.destinationCity);
                      
                      if (!originCoords || !destCoords) return null;
                      
                      const origin = latLngToXY(originCoords.lat, originCoords.lng);
                      const dest = latLngToXY(destCoords.lat, destCoords.lng);
                      
                      const isSelected = selectedRoute === req.id;
                      
                      return (
                        <g 
                          key={req.id}
                          onClick={() => setSelectedRoute(isSelected ? null : req.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          {/* Route line */}
                          <line
                            x1={origin.x}
                            y1={origin.y}
                            x2={dest.x}
                            y2={dest.y}
                            stroke={isSelected ? '#059669' : '#10b981'}
                            strokeWidth={isSelected ? 4 : 2}
                            strokeDasharray={req.flexibleDates ? '8,4' : 'none'}
                            opacity={isSelected ? 1 : 0.7}
                          />
                          
                          {/* Origin marker */}
                          <circle
                            cx={origin.x}
                            cy={origin.y}
                            r={isSelected ? 10 : 7}
                            fill="#059669"
                            stroke="white"
                            strokeWidth="2"
                          />
                          
                          {/* Destination marker */}
                          <circle
                            cx={dest.x}
                            cy={dest.y}
                            r={isSelected ? 10 : 7}
                            fill="#dc2626"
                            stroke="white"
                            strokeWidth="2"
                          />
                          
                          {/* Arrow head */}
                          <polygon
                            points={`${dest.x},${dest.y - 12} ${dest.x - 6},${dest.y - 20} ${dest.x + 6},${dest.y - 20}`}
                            fill="#dc2626"
                          />
                        </g>
                      );
                    })}
                  </svg>
                  
                  {/* Legend */}
                  <div className="flex flex-wrap gap-4 mt-4 text-xs text-gray-600">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 bg-green-600 rounded-full"></span> Start
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 bg-red-600 rounded-full"></span> Ziel
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-6 border-t-2 border-green-500"></span> Fester Termin
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-6 border-t-2 border-dashed border-green-500"></span> Flexibel
                    </span>
                  </div>
                </div>
                
                {/* Request List */}
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {filteredRequests.map((req) => (
                    <Card 
                      key={req.id}
                      className={`cursor-pointer transition-all ${
                        selectedRoute === req.id 
                          ? 'border-green-500 bg-green-50' 
                          : 'hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedRoute(selectedRoute === req.id ? null : req.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                              <span>{req.originCity}</span>
                              <ArrowRight className="h-4 w-4 text-gray-400" />
                              <span>{req.destinationCity}</span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {formatDate(req.dateFrom)} - {formatDate(req.dateTo)}
                              </span>
                              {req.flexibleDates && (
                                <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">
                                  Flexibel
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                                <PawPrint className="h-3 w-3" />
                                {req.animalCount}x {req.animalType}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                req.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {req.status === 'pending' ? 'Offen' : 
                                 req.status === 'confirmed' ? 'Bestätigt' : req.status}
                              </span>
                            </div>
                          </div>
                          <div className="text-xs text-gray-400">
                            #{req.id}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Section */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Info className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-blue-800 mb-2">So funktioniert die Kostenteilung</h3>
                <p className="text-blue-700 text-sm mb-3">
                  Bei Langstrecken-Transporten fallen normalerweise hohe Anfahrtskosten an 
                  (Sternstraße 1 → Abholung → Ziel → Sternstraße 1). Durch die Kombination 
                  von Transporten können diese Kosten geteilt werden:
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>Beispiel:</strong> Köln → Frankfurt und Frankfurt → München am gleichen Tag</li>
                  <li>• Statt 2x Hin- und Rückfahrt nur 1x durchgehende Route</li>
                  <li>• <strong>Ersparnis:</strong> Bis zu 50% der Transportkosten</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ComboTransportPage;
